import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faHome, faPencil, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { Toolbar } from '../components/toolbar/toolbar.tsx';
import { ToolbarTitle } from '../components/toolbar/toolbar-title.tsx';
import { Button } from '@progress/kendo-react-buttons';
import { useEffect, useMemo, useState } from 'react';
import { SamplerDialog } from '../dialog/sampler-dialog.tsx';
import { Layout } from '../components/layout/layout.tsx';
import { ListViewItemCell } from '../components/list/list-view-item-cell.tsx';
import { ListViewItem } from '../components/list/list-view-item.tsx';
import { ListViewItemSeparator } from '../components/list/list-view-item-separator.tsx';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ListView } from '../components/list/list-view.tsx';
import type { SamplerUser } from '../services/api.types.ts';
import type { ListViewRenderProps } from '../components/list/list-view.types.ts';
import { ErrorRender } from '../components/render/error-render.tsx';
import { ListViewItemText } from '../components/list/list-view-item-text.tsx';
import { ToolbarSeparator } from '../components/toolbar/toolbar-separator.tsx';
import { type CheckboxChangeEvent, type TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { TextBoxFilter } from '../components/input/textbox-filter.tsx';
import { getSamplers } from '../services/api.ts';
import { Checkbox } from '../components/input/checkbox.tsx';
import styles from './sampler-management-page.module.scss';

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size={'lg'} />,
  },
  {
    id: 'sampler-management',
    text: 'Sampler Management',
    url: '/sampler-management',
  },
];

export function SamplerManagementPage() {
  const [samplerUsers, setSamplerUsers] = useState<SamplerUser[]>([]);
  const [openSamplerUser, setOpenSamplerUser] = useState<SamplerUser | null>(null);
  const [inactiveFlag, setInactiveFlag] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const samplers = await getSamplers({ showInactive: false });
        if (isMounted) {
          setSamplerUsers(samplers);
        }
      } catch (error) {
        console.error('Error loading samplers:', error);
        if (isMounted) {
          setError('Unable to load samplers');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredUsers = useMemo(() => {
    const search = filter.trim().toLowerCase();

    return samplerUsers
      .reduce<SamplerUser[]>((acc, sampler) => {
        const canView = sampler.active || inactiveFlag;
        const matchesSearch =
          sampler.username.toLowerCase().includes(search) ||
          sampler.forename.toLowerCase().includes(search) ||
          sampler.surname.toLowerCase().includes(search);

        if (canView && matchesSearch) {
          acc.push({ ...sampler });
        }

        return acc;
      }, [])
      .sort((a, b) => {
        if (a.active !== b.active) {
          return a.active ? -1 : 1;
        }

        const forenameCompare = a.forename.localeCompare(b.forename);
        if (forenameCompare !== 0) {
          return forenameCompare;
        }

        return a.surname.localeCompare(b.surname);
      });
  }, [filter, inactiveFlag, samplerUsers]);

  const renderListItem = ({ dataItem }: ListViewRenderProps<SamplerUser>) => {
    const handleEdit = () => {
      setOpenSamplerUser(dataItem);
    };

    return (
      <ListViewItem>
        {dataItem.active ? (
          <>
            <ListViewItemCell flex={0}>
              <FontAwesomeIcon className={styles.activeItem} icon={faCheck} size={'lg'} />
            </ListViewItemCell>
          </>
        ) : (
          <>
            <ListViewItemCell flex={0}>
              <FontAwesomeIcon className={styles.inactiveItem} icon={faCheck} size={'lg'} />
            </ListViewItemCell>
          </>
        )}
        <ListViewItemSeparator />
        <ListViewItemText flex={2}>
          <strong>{dataItem.username}</strong>
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemText>
          <strong>Forename:&nbsp;</strong>
          {dataItem.forename}
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemText>
          <strong>Surname:&nbsp;</strong>
          {dataItem.surname}
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemText>
          <strong>Working Days:&nbsp;</strong>
          {dataItem.weeklyWorkdays}
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemText>
          <strong>Daily Samples:&nbsp;</strong>
          {dataItem.weeklySampleEstimate}
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemCell flex={0}>
          <Button themeColor={'secondary'} onClick={handleEdit}>
            <FontAwesomeIcon icon={faPencil} size={'1x'} />
          </Button>
        </ListViewItemCell>
      </ListViewItem>
    );
  };

  const handleFlagChange = async ({ value }: CheckboxChangeEvent) => {
    setLoading(true);
    try {
      const samplers = await getSamplers({ showInactive: value });
      setSamplerUsers(samplers);
    } catch (error) {
      console.error('Error loading samplers:', error);
      setError('Unable to load samplers');
    } finally {
      setInactiveFlag(value);
      setLoading(false);
    }
  };

  const handleFilterChange = (e: TextBoxChangeEvent) => {
    setFilter(String(e.value));
  };

  const handleFilterClear = () => {
    setFilter('');
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const samplers = await getSamplers({ showInactive: true });
      setSamplerUsers(samplers);
    } catch (error) {
      console.error('Error loading samplers:', error);
      setError('Unable to load samplers');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogApply = async () => {
    try {
      const samplers = await getSamplers();
      setSamplerUsers(samplers);
    } catch (error) {
      console.error('Error loading samplers:', error);
      setError('Unable to load samplers');
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setOpenSamplerUser(null);
  };

  return (
    <>
      <Layout breadcrumb={BREADCRUMB_ITEMS}>
        <Toolbar>
          <ToolbarTitle flex={1}>Sampler Management</ToolbarTitle>
          <ToolbarSeparator />
          <TextBoxFilter
            value={filter}
            onChange={handleFilterChange}
            onClear={handleFilterClear}
            style={{ width: 280 }}
            placeholder={'Filter by username, forename or surname..'}
          />
          <ToolbarSeparator />
          <Checkbox checked={inactiveFlag} label={'Show Inactive'} onChange={handleFlagChange} />
          <ToolbarSeparator />
          <Button themeColor={'primary'} className={styles.refreshBtn} onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} size={'1x'} />
          </Button>
        </Toolbar>
        {loading ? (
          <LoaderRender message={'Loading...'} />
        ) : error.length > 0 ? (
          <ErrorRender message={error} />
        ) : (
          <div className={styles.content}>
            <ListView<SamplerUser>
              className={styles.list}
              data={filteredUsers}
              item={renderListItem}
              emptyMessage={'No sampler data found, please ensure filters are correctly set.'}
              emptyClassName={styles.emptyMessage}
            />
          </div>
        )}
      </Layout>
      {openSamplerUser && (
        <SamplerDialog
          sampler={openSamplerUser}
          onApply={handleDialogApply}
          onClose={handleDialogClose}
        ></SamplerDialog>
      )}
    </>
  );
}
